import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Divider,
  Typography,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import HomeIcon from '@mui/icons-material/Home';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import { styled } from '@mui/material/styles';
import { useCart } from '../contexts/CartContext';
import logosquare from '../assets/logosquare.jpg';
import partenaire from '../assets/partenaire.png';

/* =======================
   TOP LOGO BAR
======================= */

const TopLogoBar = styled(Box)(() => ({
  background: '#ffffff',
  padding: '16px 0',
  borderBottom: '1px solid rgba(0,0,0,0.05)',
}));

/* =======================
   NAVBAR ACRYLIQUE
======================= */

const StyledAppBar = styled(AppBar)(() => ({
  background: 'rgba(255, 255, 255, 0.75)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  backgroundColor: 'rgba(255, 255, 255, 0.65)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  color: '#111',
  height: 72,
  justifyContent: 'center',
}));

/* NAV BUTTONS */
const NavButton = styled(Button)(({ theme }) => ({
  color: '#111',
  fontWeight: 700,
  fontSize: '14px',
  textTransform: 'uppercase',
  padding: '8px 16px',
  position: 'relative',
  borderRadius: '8px',
  minWidth: 'auto',
  letterSpacing: '0.5px',
  
  '&:hover': {
    backgroundColor: 'rgba(198, 40, 40, 0.15)',
    color: '#c62828',
    backdropFilter: 'blur(10px)',
  },
  
  '&.active': {
    color: '#c62828',
    backgroundColor: 'rgba(198, 40, 40, 0.12)',
    backdropFilter: 'blur(10px)',
  },
  
  [theme.breakpoints.down('md')]: {
    fontSize: '13px',
    padding: '6px 12px',
  },
}));

/* HOME ICON BUTTON */
const HomeIconButton = styled(IconButton)(({ theme }) => ({
  color: '#111',
  padding: '10px',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.4)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(10px)',
  
  '&:hover': {
    backgroundColor: 'rgba(198, 40, 40, 0.15)',
    color: '#c62828',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: '8px',
  },
}));

/* CTA BUTTON */
const PrimaryButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #c62828, #8e0000)',
  color: '#fff',
  padding: '10px 20px',
  fontWeight: 800,
  fontSize: '14px',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  borderRadius: '10px',
  boxShadow: '0 4px 15px rgba(198,40,40,0.25)',
  transition: 'all 0.3s ease',
  whiteSpace: 'nowrap',
  minWidth: 'auto',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.1)',

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(198,40,40,0.35)',
    background: 'linear-gradient(135deg, #b71c1c, #7f0000)',
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    padding: '8px 16px',
    minWidth: '120px',
  },
}));

/* SOCIAL ICONS ACRYLIQUE */
const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: '#555',
  padding: '6px',
  borderRadius: '8px',
  backdropFilter: 'blur(10px)',
  backgroundColor: 'rgba(255, 255, 255, 0.4)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',

  '&:hover': {
    color: '#c62828',
    transform: 'translateY(-2px)',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  
  [theme.breakpoints.down('md')]: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
  },
}));

/* CONTACT INFO */
const ContactInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  fontSize: '14px',
  
  [theme.breakpoints.down('md')]: {
    gap: '8px',
    fontSize: '12px',
  },
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  borderRadius: '8px',
  backgroundColor: 'rgba(198, 40, 40, 0.05)',
  border: '1px solid rgba(198, 40, 40, 0.1)',
  
  [theme.breakpoints.down('md')]: {
    padding: '4px 8px',
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: '3px 6px',
    fontSize: '11px',
  },
}));

/* CART ICON BUTTON */
const CartIconButton = styled(IconButton)(({ theme }) => ({
  color: '#111',
  padding: '10px',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.4)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(10px)',
  
  '&:hover': {
    backgroundColor: 'rgba(198, 40, 40, 0.15)',
    color: '#c62828',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  
  [theme.breakpoints.down('sm')]: {
    padding: '8px',
  },
}));

/* =======================
   COMPONENT
======================= */

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { getCartCount } = useCart();

  const navItems = [
    { label: 'Formations', path: '/formations' },
    { label: 'À propos', path: '/about' },
    { label: 'Emplacement', path: '/emplacement' },
    { label: 'FAQ', path: '/faq' },
  ];

  return (
    <>
      {/* ===== TOP LOGO BAR ===== */}
      <TopLogoBar>
        <Container maxWidth="xl">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            {/* LEFT : LOGO */}
            <Box 
              display="flex" 
              alignItems="center"
              sx={{ 
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  justifyContent: 'center'
                }
              }}
            >
              <Link to="/">
                <img
                  src={logosquare}
                  alt="Académie Vita Secours"
                  style={{ 
                    height: 'clamp(80px, 10vw, 120px)',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease',
                    borderRadius: '8px',
                    maxWidth: '100%',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                />
              </Link>
            </Box>

            {/* CENTER : CONTACT INFO */}
            <ContactInfo
              sx={{ 
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  justifyContent: 'center',
                  order: 3,
                  mt: 1,
                }
              }}
            >
              <ContactItem>
                <PhoneIcon sx={{ fontSize: 'clamp(14px, 2vw, 18px)', color: '#c62828' }} />
                <Box fontWeight={700}>450 300-5005</Box>
              </ContactItem>
              
              <ContactItem>
                <EmailIcon sx={{ fontSize: 'clamp(14px, 2vw, 18px)', color: '#c62828' }} />
                <Box>info@vitasecours.ca</Box>
              </ContactItem>
            </ContactInfo>

            {/* RIGHT : PARTNER LOGO */}
            <Box
              sx={{ 
                [theme.breakpoints.down('sm')]: {
                  position: 'absolute',
                  right: 16,
                  top: 16,
                }
              }}
            >
              <img
                src={partenaire}
                alt="Partenaire Croix-Rouge"
                style={{ 
                  height: 'clamp(45px, 6vw, 75px)',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  maxWidth: '100%',
                }}
              />
            </Box>
          </Box>
        </Container>
      </TopLogoBar>

      {/* ===== NAVBAR ACRYLIQUE ===== */}
      <StyledAppBar position="sticky">
        <Container maxWidth="xl">
          <Toolbar sx={{ 
            justifyContent: 'space-between', 
            minHeight: '72px !important',
            gap: 2,
            px: { xs: 1, sm: 2, md: 3 },
          }}>
            {/* LEFT : HOME ICON + MENU ITEMS */}
            <Box display="flex" alignItems="center" gap={2}>
              <HomeIconButton 
                component={Link}
                to="/"
                sx={{ 
                  [theme.breakpoints.down('sm')]: {
                    padding: '6px',
                  }
                }}
              >
                <HomeIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
              </HomeIconButton>

              {!isMobile && (
                <Box display="flex" gap={0.5}>
                  {navItems.map((item) => (
                    <NavButton 
                      key={item.label} 
                      component={Link} 
                      to={item.path}
                    >
                      {item.label}
                    </NavButton>
                  ))}
                </Box>
              )}
            </Box>

            {/* RIGHT : ACTIONS */}
            <Box display="flex" alignItems="center" gap={1}>
              {/* SOCIAL ICONS (desktop) */}
              {!isMobile && (
                <Box display="flex" gap={0.5}>
                  <SocialIcon href="https://www.facebook.com/profile.php?id=61585390049859" target="_blank">
                    <FacebookIcon fontSize="small" />
                  </SocialIcon>
                  <SocialIcon href="https://www.instagram.com/academie_vita_secours/" target="_blank">
                    <InstagramIcon fontSize="small" />
                  </SocialIcon>
                  <SocialIcon href="https://x.com/AcademieS36807" target="_blank">
                    <XIcon fontSize="small" />
                  </SocialIcon>
                  <SocialIcon href="https://www.youtube.com/channel/UCanoHxkJMEtPC9ilhDqqdxw" target="_blank">
                    <YouTubeIcon fontSize="small" />
                  </SocialIcon>
                  <SocialIcon href="https://www.linkedin.com/in/acad%C3%A9mie-vita-secours-539b4b3bb/" target="_blank">
                    <LinkedInIcon fontSize="small" />
                  </SocialIcon>
                </Box>
              )}

              {/* SHOPPING CART ICON */}
              <CartIconButton 
                component={Link}
                to="/panier"
                sx={{ 
                  [theme.breakpoints.down('sm')]: {
                    padding: '6px',
                  }
                }}
              >
                <Badge badgeContent={getCartCount()} color="error">
                  <ShoppingCartIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                </Badge>
              </CartIconButton>

              {/* BOUTON "NOUS JOINDRE" */}
              <PrimaryButton 
                component={Link} 
                to="/contact"
                sx={{ 
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                Nous joindre
              </PrimaryButton>

              {/* MENU MOBILE */}
              {isMobile && (
                <>
                  <PrimaryButton 
                    component={Link} 
                    to="/contact"
                    sx={{ 
                      display: { xs: 'flex', sm: 'none' },
                      fontSize: '11px',
                      padding: '6px 12px',
                      minWidth: '100px',
                    }}
                  >
                    Nous joindre
                  </PrimaryButton>
                  
                  <IconButton 
                    onClick={() => setDrawerOpen(true)}
                    sx={{ 
                      ml: 0.5,
                      backgroundColor: 'rgba(255, 255, 255, 0.5)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '8px',
                      padding: '8px',
                    }}
                  >
                    <MenuIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
                  </IconButton>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* ===== DRAWER MOBILE ACRYLIQUE ===== */}
      <Drawer 
        anchor="right" 
        open={drawerOpen} 
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { 
            width: { xs: '280px', sm: '320px' },
            maxWidth: '85vw',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px) saturate(180%)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.18)',
          }
        }}
      >
        <Box p={2}>
          {/* HEADER */}
          <Box 
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" fontWeight={700} color="#c62828">
              Menu
            </Typography>
            <IconButton 
              onClick={() => setDrawerOpen(false)}
              sx={{ 
                backgroundColor: 'rgba(198, 40, 40, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(198, 40, 40, 0.2)',
                }
              }}
            >
              <HomeIcon />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2, borderColor: 'rgba(0,0,0,0.1)' }} />

          {/* MENU ITEMS */}
          <List sx={{ p: 0 }}>
            <ListItem
              component={Link}
              to="/"
              onClick={() => setDrawerOpen(false)}
              sx={{
                color: '#111',
                textDecoration: 'none',
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                mb: 1,
                backgroundColor: 'rgba(198, 40, 40, 0.05)',
                border: '1px solid rgba(198, 40, 40, 0.1)',
                
                '&:hover': {
                  backgroundColor: 'rgba(198, 40, 40, 0.15)',
                  color: '#c62828',
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <HomeIcon sx={{ fontSize: 20 }} />
                <Typography fontWeight={600}>
                  Accueil
                </Typography>
              </Box>
            </ListItem>

            {navItems.map((item) => (
              <ListItem
                key={item.label}
                component={Link}
                to={item.path}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  color: '#111',
                  textDecoration: 'none',
                  py: 1.5,
                  px: 2,
                  borderRadius: '8px',
                  mb: 1,
                  borderLeft: '3px solid transparent',
                  transition: 'all 0.3s ease',
                  backgroundColor: 'rgba(255, 255, 255, 0.4)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  
                  '&:hover': {
                    backgroundColor: 'rgba(198, 40, 40, 0.15)',
                    color: '#c62828',
                    borderLeft: '3px solid #c62828',
                    paddingLeft: '20px',
                  },
                }}
              >
                <Typography fontWeight={600}>
                  {item.label}
                </Typography>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2, borderColor: 'rgba(0,0,0,0.1)' }} />

          {/* CONTACT INFO MOBILE */}
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Contact rapide
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <PhoneIcon fontSize="small" sx={{ color: '#c62828' }} />
              <Typography variant="body2">450 300-5005</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <EmailIcon fontSize="small" sx={{ color: '#c62828' }} />
              <Typography variant="body2">info@vitasecours.ca</Typography>
            </Box>
          </Box>

          {/* SOCIAL ICONS MOBILE */}
          <Box mb={2}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Suivez-nous
            </Typography>
            <Box display="flex" justifyContent="center" gap={1}>
              <SocialIcon href="https://www.facebook.com/profile.php?id=61585390049859" target="_blank">
                <FacebookIcon />
              </SocialIcon>
              <SocialIcon href="https://www.instagram.com/academie_vita_secours/" target="_blank">
                <InstagramIcon />
              </SocialIcon>
              <SocialIcon href="https://x.com/AcademieS36807" target="_blank">
                <XIcon />
              </SocialIcon>
              <SocialIcon href="https://www.youtube.com/channel/UCanoHxkJMEtPC9ilhDqqdxw" target="_blank">
                <YouTubeIcon />
              </SocialIcon>
              <SocialIcon href="https://www.linkedin.com/in/acad%C3%A9mie-vita-secours-539b4b3bb/" target="_blank">
                <LinkedInIcon />
              </SocialIcon>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;